import newCallSound from "../assets/new-call.mp3";
import chatMessageWav from "../assets/sounds/notifications/chat_message.wav";
import newRequestWav from "../assets/sounds/notifications/new_request.wav";
import requestAssignedWav from "../assets/sounds/notifications/request_assigned.wav";
import teamMessageWav from "../assets/sounds/notifications/team_message.wav";
import teamMentionWav from "../assets/sounds/notifications/team_mention.wav";
import agendaReminderWav from "../assets/sounds/notifications/agenda_reminder.wav";
import taskDueWav from "../assets/sounds/notifications/task_due.wav";
import taskOverdueWav from "../assets/sounds/notifications/task_overdue.wav";
import systemWav from "../assets/sounds/notifications/system.wav";
import billingWav from "../assets/sounds/notifications/billing.wav";
import securityWav from "../assets/sounds/notifications/security.wav";
import legacySocketWav from "../assets/sounds/notifications/legacy_socket.wav";
import { NOTIFICATION_TYPES } from "./types";
import {
  GROUP_PREVIEW_SOUND_TYPE,
  NOTIFICATION_SOUND_VOLUME,
  NOTIFICATION_TONE_PROFILES,
} from "./soundProfiles";

export { GROUP_PREVIEW_SOUND_TYPE };

let audioUnlocked = false;
let preloadStarted = false;
/** @type {AudioContext | null} */
let sharedCtx = null;
/** @type {Map<string, string>} */
const soundAssetUrls = new Map([
  [NOTIFICATION_TYPES.chat_message, chatMessageWav],
  [NOTIFICATION_TYPES.new_request, newRequestWav],
  [NOTIFICATION_TYPES.request_assigned, requestAssignedWav],
  [NOTIFICATION_TYPES.response_sla, newRequestWav],
  [NOTIFICATION_TYPES.voice_queue, newRequestWav],
  [NOTIFICATION_TYPES.voice_call, newRequestWav],
  [NOTIFICATION_TYPES.team_message, teamMessageWav],
  [NOTIFICATION_TYPES.team_mention, teamMentionWav],
  [NOTIFICATION_TYPES.agenda_reminder, agendaReminderWav],
  [NOTIFICATION_TYPES.task_due, taskDueWav],
  [NOTIFICATION_TYPES.task_overdue, taskOverdueWav],
  [NOTIFICATION_TYPES.task_completed, taskDueWav],
  [NOTIFICATION_TYPES.task_board_invite, requestAssignedWav],
  [NOTIFICATION_TYPES.system, systemWav],
  [NOTIFICATION_TYPES.billing, billingWav],
  [NOTIFICATION_TYPES.security, securityWav],
  [NOTIFICATION_TYPES.legacy_socket, legacySocketWav],
]);

const CRITICAL_FALLBACK_TYPES = new Set([
  NOTIFICATION_TYPES.new_request,
  NOTIFICATION_TYPES.voice_queue,
  NOTIFICATION_TYPES.request_assigned,
  NOTIFICATION_TYPES.security,
]);

export function unlockNotificationAudio() {
  audioUnlocked = true;
  preloadNotificationSounds();
  void ensureAudioContextRunning();
}

export function isNotificationAudioUnlocked() {
  return audioUnlocked;
}

/** Pré-carrega WAVs no primeiro gesto do usuário (melhora latência e confiabilidade). */
export function preloadNotificationSounds() {
  if (preloadStarted || typeof window === "undefined") return;
  preloadStarted = true;
  for (const url of soundAssetUrls.values()) {
    if (!url) continue;
    try {
      const audio = new Audio(url);
      audio.preload = "auto";
      audio.load();
    } catch {
      /* ignore */
    }
  }
}

function getAudioContext() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    if (!sharedCtx || sharedCtx.state === "closed") {
      sharedCtx = new Ctx();
    }
    return sharedCtx;
  } catch {
    return null;
  }
}

/** @returns {Promise<boolean>} */
async function ensureAudioContextRunning() {
  const ctx = getAudioContext();
  if (!ctx) return false;
  if (ctx.state === "suspended") {
    try {
      await ctx.resume();
    } catch {
      return false;
    }
  }
  if (ctx.state === "running") {
    audioUnlocked = true;
    return true;
  }
  return false;
}

/** @param {import("./soundProfiles.js").ToneStep[]} steps @returns {Promise<boolean>} */
async function playToneSequence(steps) {
  const ready = await ensureAudioContextRunning();
  if (!ready) return false;

  const ctx = getAudioContext();
  if (!ctx) return false;

  for (const step of steps) {
    const {
      freq,
      start = 0,
      duration = 0.1,
      gain = 0.065,
      wave = "sine",
    } = step;
    if (!freq || duration <= 0) continue;

    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    osc.type = wave;
    osc.frequency.value = freq;

    const t0 = ctx.currentTime + start;
    const attack = Math.min(0.012, duration * 0.25);
    gainNode.gain.setValueAtTime(0.0001, t0);
    gainNode.gain.linearRampToValueAtTime(gain, t0 + attack);
    gainNode.gain.exponentialRampToValueAtTime(0.0001, t0 + duration);

    osc.connect(gainNode);
    gainNode.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + duration + 0.03);
  }

  return true;
}

/** @param {string} type @returns {Promise<boolean>} */
async function playToneProfileForType(type) {
  const steps =
    NOTIFICATION_TONE_PROFILES[type] || NOTIFICATION_TONE_PROFILES[NOTIFICATION_TYPES.system];
  return playToneSequence(steps);
}

function volumeForType(type) {
  return NOTIFICATION_SOUND_VOLUME[type] ?? NOTIFICATION_SOUND_VOLUME.system ?? 0.72;
}

/** @param {string} url @param {number} volume @returns {Promise<boolean>} */
export function playMp3Url(url, volume = 1) {
  if (!url) return Promise.resolve(false);
  try {
    const audio = new Audio(url);
    audio.volume = Math.min(1, Math.max(0, volume));
    return audio
      .play()
      .then(() => {
        audioUnlocked = true;
        return true;
      })
      .catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
}

/** @param {string} type @returns {Promise<boolean>} */
async function playWavForType(type) {
  const url = soundAssetUrls.get(type) || soundAssetUrls.get(NOTIFICATION_TYPES.system);
  if (!url) return false;
  try {
    const audio = new Audio(url);
    audio.volume = volumeForType(type);
    await audio.play();
    audioUnlocked = true;
    return true;
  } catch {
    return false;
  }
}

/**
 * @param {import("./types").NotificationType | string} type
 * @returns {Promise<boolean>}
 */
export async function playAlertSoundForType(type) {
  const resolved = soundAssetUrls.has(type) ? type : NOTIFICATION_TYPES.system;

  const fromWav = await playWavForType(resolved);
  if (fromWav) return true;

  const fromTone = await playToneProfileForType(resolved);
  if (fromTone) return true;

  if (CRITICAL_FALLBACK_TYPES.has(resolved)) {
    return playMp3Url(getRequestAlertSoundUrl(), 0.45);
  }

  return playMp3Url(getRequestAlertSoundUrl(), 0.35);
}

/** @deprecated use playAlertSoundForType — mantido para compat pontual */
export function playSoftMessageBeep() {
  void playAlertSoundForType(NOTIFICATION_TYPES.chat_message);
}

/** Som de chamada telefônica / voz (não confundir com alertas de fila). */
export function getCallAlertSoundUrl() {
  return newCallSound;
}

/** @type {HTMLAudioElement | null} */
let incomingRingtone = null;

/** Um único toque em loop. Sempre pare ao atender, recusar ou encerrar. */
export function startIncomingCallRingtone() {
  if (typeof Audio === "undefined") return;
  stopIncomingCallRingtone();
  const audio = new Audio(newCallSound);
  audio.loop = true;
  incomingRingtone = audio;
  audio.play().catch(() => {});
}

export function stopIncomingCallRingtone() {
  const audio = incomingRingtone;
  incomingRingtone = null;
  if (!audio) return;
  try {
    audio.pause();
    audio.loop = false;
    audio.currentTime = 0;
  } catch {
    /* ignore */
  }
}

/** @deprecated alias — fallback de emergência para fila/atribuição */
export function getRequestAlertSoundUrl() {
  return newCallSound;
}

/** @deprecated mantido para compat — perfis migrados para soundProfiles.js */
export const ALERT_SOUND_PROFILES = NOTIFICATION_TONE_PROFILES;
